import type { FilterState, Place } from '@/types';
import { Category } from '@/types';

function isCoordinate(value: unknown): value is Place['coordinates'][number] {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const coordinate = value as Partial<Place['coordinates'][number]>;
  return (
    typeof coordinate.lat === 'number' &&
    typeof coordinate.lon === 'number' &&
    typeof coordinate.alt === 'number'
  );
}

/**
 * Filter places based on search query and selected categories
 */
export function filterPlaces(
  places: Place[],
  filterState: FilterState
): Place[] {
  return places.filter((place) => {
    // Filter by categories
    const categoryMatch =
      filterState.selectedCategories.size === 0 ||
      place.categories.some((cat) => filterState.selectedCategories.has(cat));

    // Filter by search query
    const searchMatch =
      filterState.searchQuery === '' ||
      place.title.toLowerCase().includes(filterState.searchQuery.toLowerCase()) ||
      place.description.toLowerCase().includes(filterState.searchQuery.toLowerCase());

    return categoryMatch && searchMatch;
  });
}

/**
 * Get count of places for each category
 */
export function getCategoryCounts(places: Place[]): Map<Category, number> {
  const counts = new Map<Category, number>();

  places.forEach((place) => {
    place.categories.forEach((category) => {
      counts.set(category, (counts.get(category) || 0) + 1);
    });
  });

  return counts;
}

/**
 * Debounce function for search input
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate unique ID for places if not provided
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validate place data
 */
export function validatePlace(place: unknown): place is Place {
  if (typeof place !== 'object' || place === null) {
    return false;
  }

  const p = place as Partial<Place>;

  return (
    typeof p.title === 'string' &&
    typeof p.description === 'string' &&
    Array.isArray(p.coordinates) &&
    p.coordinates.length > 0 &&
    p.coordinates.every((coordinate) => isCoordinate(coordinate)) &&
    Array.isArray(p.categories) &&
    p.categories.length > 0
  );
}

export function getAllCategories(): Category[] {
  return Object.values(Category).filter((value): value is Category => typeof value === 'number');
}
